import React, { FunctionComponent, useEffect, useState } from 'react';
import TermsBox from 'app/components/auth/TermsBox';
import { FormattedTextWithNewlines } from 'app/components/common/FormattedText';
import { I18n, LocalizationProvider } from 'app/components/localization';
import { CheckboxField } from 'app/components/presentation/forms/Fields';
import MarginGroup from 'app/components/layout/MarginGroup';
import Separator from 'app/components/layout/Separator';
import { LocalizationService } from 'app/services/localization';
import { GREY_G } from 'app/constants/Colors';
import {
  KoreanTermsGeneral,
  KoreanTermsPersonalInfo,
  KoreanTermsPersonalInfoUsage,
} from 'app/constants/KoreanTermsContent';
import { useCss } from 'app/utils/css';

interface IProps {
  showValidationErrors: boolean;
  setAgree: (checked: boolean) => void;
}

/**
 * Renders the Korean Terms of a Agreement for signup.
 */
const TermsAgreementKorea: FunctionComponent<IProps> = ({
  setAgree,
  showValidationErrors,
}) => {
  const css = useCss();
  const [agreeKoreanAll, setAgreeKoreanAll] = useState(false);
  const [agreeKoreanEngoo, setAgreeKoreanEngoo] = useState(false);
  const [agreeKoreanEmail, setAgreeKoreanEmail] = useState(false);
  const [agreeKoreanPersonalInfoProcessing, setAgreeKoreanPersonalInfoProcessing] = useState(false);
  const [agreeKoreanPersonalInfoUsage, setAgreeKoreanPersonalInfoUsage] = useState(false);

  const initLocalizationService = () => {
    const localizationService = new LocalizationService();
    localizationService.setFallback('ko');
    return localizationService;
  };
  const [localizationService] = useState(initLocalizationService());

  const renderKoreanCheckbox = (label: string, checkedState: boolean, setChecked: (checked: boolean) => void) => {
    return (
      <I18n>{({ t }) =>
        <CheckboxField
          data-agree-checkbox
          label={label}
          onToggle={(checked: boolean) => {
            if (!checked) {
              setAgreeKoreanAll(false);
            }
            setChecked(checked);
          }}
          checked={checkedState}
          validationError={(showValidationErrors && !checkedState) ?
            t('auth.terms-agreement.validation-agree') : undefined}
        />
      }</I18n>
    );
  };

  const setAgreeAll = (checked: boolean) => {
    setAgree(checked);
    setAgreeKoreanAll(checked);
    setAgreeKoreanEngoo(checked);
    setAgreeKoreanEmail(checked);
    setAgreeKoreanPersonalInfoProcessing(checked);
    setAgreeKoreanPersonalInfoUsage(checked);
  };

  useEffect(() => {
    setAgree(
      agreeKoreanEngoo
      && agreeKoreanEmail
      && agreeKoreanPersonalInfoProcessing
      && agreeKoreanPersonalInfoUsage,
    );
  }, [setAgree, agreeKoreanEngoo, agreeKoreanEmail, agreeKoreanPersonalInfoProcessing, agreeKoreanPersonalInfoUsage]);

  return (
    <LocalizationProvider service={localizationService}>
      <I18n>{({ t }) =>
        <MarginGroup margin="2em">
          <TermsBox
            title={t('auth.terms-agreement.terms-header')}
            contents={<FormattedTextWithNewlines text={KoreanTermsGeneral} />}
          />
          <TermsBox
            title={t('auth.terms-agreement.personal-information-terms-header')}
            contents={<FormattedTextWithNewlines text={KoreanTermsPersonalInfo} />}
          />
          <div>
            <TermsBox
              title={t('auth.terms-agreement.personal-information-usage-header')}
              contents={<FormattedTextWithNewlines text={KoreanTermsPersonalInfoUsage} />}
            />
            <div
              className={css('note', {
                marginTop: '1em',
                color: GREY_G,
                fontSize: '0.8em',
              })}
            >
              {t('auth.terms-agreement.note')}
            </div>
          </div>
          <MarginGroup margin="1em">
            <CheckboxField
              data-testid="terms-agreement-korea"
              data-checkbox
              label={
                t('auth.terms-agreement.agree-check-all')
              }
              onToggle={setAgreeAll}
              checked={agreeKoreanAll}
            />
            <Separator />
            <MarginGroup margin="1em">
              {renderKoreanCheckbox(t('auth.terms-agreement.agree-skype'), agreeKoreanEngoo, setAgreeKoreanEngoo)}
              {renderKoreanCheckbox(t('auth.terms-agreement.agree-email'), agreeKoreanEmail, setAgreeKoreanEmail)}
              {renderKoreanCheckbox(
                t('auth.terms-agreement.agree-personal-processing'),
                agreeKoreanPersonalInfoProcessing,
                setAgreeKoreanPersonalInfoProcessing)}
              {renderKoreanCheckbox(
                t('auth.terms-agreement.agree-personal-usage'),
                agreeKoreanPersonalInfoUsage,
                setAgreeKoreanPersonalInfoUsage)}
            </MarginGroup>
          </MarginGroup>
        </MarginGroup>
      }</I18n>
    </LocalizationProvider>
  );
};

export default TermsAgreementKorea;
